/**
 * The allergy list.
 *
 * Replaces the `Allergy` keyon envelope — `GetPatientAllergies`, `GetAllergies`,
 * `InsertNewAllergy`, `AddAllergies`, `UpdateAllergyInfo` — and the three
 * duplicates that shipped under `MedicationAdmin`: `getPatientAllergies`,
 * `AddPatientAllergyInfo` and `UpdatePatientAllergyInfo`. Six operations for one
 * list, and only one of them was audited.
 *
 * Allergies and the problem list share the `conditions` table, discriminated by
 * category, and every query runs through a domain service that takes the
 * category as an enum rather than a string. That is not bookkeeping: an allergy
 * that fails to appear on the allergy list, or a diagnosis that appears on it,
 * is a patient-safety defect. An id of the wrong category is a 404 — "that
 * record exists, just not here" is itself a disclosure.
 */

import api from './client';

/** `?filter[status]`, `[criticality]`, `[severity]`, `[verification_status]`. */
export function listAllergies(patientId, params = {}) {
  return api.list(`/patients/${patientId}/allergies`, params);
}

/**
 * Every allergy on the chart, read to the last page.
 *
 * The allergy panel is the list a clinician scans before prescribing, and
 * main's getPatientAllergies had no LIMIT. The API pages (25 by default, 100
 * at most), so one request showed the first page of a longer list and hid the
 * rest from the screen that exists to show all of them. A page that fails
 * fails the whole read: the pages already in hand are not a list the screen
 * may show as complete.
 */
export async function listAllAllergies(patientId, params = {}) {
  const items = [];

  // Bounded: 200 pages of 100 is far past any chart, and a loop that could
  // not end is not a list.
  for (let page = 1; page <= 200; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const result = await api.list(`/patients/${patientId}/allergies`, { ...params, page, per_page: 100 });
    items.push(...(result.items || []));

    const last = result.meta && Number(result.meta.last_page);
    if (!last || page >= last) break;
  }

  return { items, meta: null, links: null };
}

export function createAllergy(patientId, payload) {
  return api.post(`/patients/${patientId}/allergies`, payload);
}

/**
 * Nested under the chart, not addressed on its own: the category check needs
 * both ids, and an allergy id from another patient's chart must not resolve.
 */
export function updateAllergy(patientId, allergyId, patch) {
  return api.patch(`/patients/${patientId}/allergies/${allergyId}`, patch);
}

export default {
  listAllergies, listAllAllergies, createAllergy, updateAllergy,
};
